// The plethysmograph trace: one pulse wave per heartbeat, timed from the synthesised ECG's R peaks.
// The wave rises steeply to the systolic peak, shows a dicrotic wave, then runs off in diastole.

/** Pulse transit: how long after the R peak the pressure wave reaches the finger, in seconds. */
export const PULSE_TRANSIT_S = 0.21;

/** One pulse wave, u seconds after its foot; 0 before the foot and once it has run off. Peak ≈ 1. */
export function plethPulse(u: number): number {
  if (u <= 0 || u > 1.6) return 0;
  const tau = 0.13;
  const k = 3.2;
  const x = u / tau;
  const systolic = Math.pow(x, k) * Math.exp(k * (1 - x)); // gamma-shaped: steep rise, slower fall, peak 1 at u = tau
  const d = (u - 0.36) / 0.075;
  const dicrotic = 0.3 * Math.exp(-0.5 * d * d);
  const runoff = Math.exp(-Math.max(0, u - 0.45) * 3.4);
  return (systolic + dicrotic) * runoff;
}

/**
 * Pleth height at time t from the R peaks around it. Beats that follow a short RR fill less, as in AF.
 * `beats` are R-peak times with the RR interval that led to each; `rr0` is the mean RR.
 */
export function plethAt(t: number, beats: ReadonlyArray<{ t: number; rr: number }>, rr0: number): number {
  let y = 0;
  for (const b of beats) {
    const u = t - b.t - PULSE_TRANSIT_S;
    if (u <= 0 || u > 1.6) continue;
    const fill = rr0 > 0 ? Math.min(1.12, Math.max(0.55, b.rr / rr0)) : 1;
    y += plethPulse(u) * fill;
  }
  return y;
}
